import { PropsWithChildren, useCallback, useContext } from 'react';
import { neutralWhite, neutral200, neutral400, primary400, primary800 } from '../themes/colors';
import styled, { css, keyframes } from 'styled-components';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { Modal } from '../modal';
import { DialogParentContext } from './DialogParentContext';
import { Icon } from '../icon';
import { Button } from '../button';
import { DialogProgress } from './DialogProgress';

const StyledModal = styled(Modal)<{
	$hasParent: boolean
}>`
	${({ $hasParent }) => $hasParent ? css`
	position: absolute;
	` : ''}
`;

// Keep in step with closeTimeoutMS below, otherwise the exit is cut off mid-slide
const ANIMATION_MS = 180;

const slideUp = keyframes`
	from { opacity: 0; transform: translateY(8px); }
`;

const slideDown = keyframes`
	to { opacity: 0; transform: translateY(8px); }
`;

const StyledDialog = styled.div`
	width: 360px;
	background: ${neutralWhite};
	/* ponytail: same literal as theme.aiChat.card.shadow; make it a shared token if a 3rd surface needs it */
	box-shadow: 0px 4px 16px 0px rgba(2, 23, 51, 0.12);
	border: 1px solid ${neutral200};
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	border-radius: 4px;
	font-family: ${p => p.theme.font};
	animation: ${slideUp} ${ANIMATION_MS}ms ease-out;

	/* react-modal stamps this on the content wrapper while it waits out closeTimeoutMS */
	.ReactModal__Content--before-close & {
		animation: ${slideDown} ${ANIMATION_MS}ms ease-in forwards;
	}

	@media (prefers-reduced-motion: reduce) {
		&, .ReactModal__Content--before-close & {
			animation: none;
		}
	}
`;

const StyledHeader = styled.div`
	display: flex;
	gap: 12px;
`;

const StyledTitleContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 5px;
`;

const StyledTitle = styled.div`
	color: ${primary800};
	font-size: 16px;
	font-weight: 500;
`;

const StyledMessage = styled.div`
	color: ${primary400};
	font-size: 14px;
`;

const StyledSubTitle = styled.div`
	color: ${primary800};
	font-size: 14px;
	font-weight: 500;
`;

const StyledCloseIcon = styled(Icon)`
	cursor: pointer;
	color: ${neutral400};
	font-size: 13px;

	&:hover {
		color: ${primary800};
	}
`;

const StyledActions = styled.div`
	display: flex;
	gap: 8px;
	padding-top: 8px;
`;

const StyledAction = styled(Button)`
	flex: 1;
	font-size: 14px;
`;

const StyledProgressContainer = styled.div`
	padding: 5px 0;
`;

export type DialogProps = {
	progress?: number,
	overhead?: string,
	title: string,
	open: boolean,
	onClose: () => unknown
};

export const Dialog = ({ 
	title,
	open,
	className,
	children,
	onClose,
	progress,
}: PropsWithChildren<PropsWithClassName<DialogProps>>) => {
	const { parent } = useContext(DialogParentContext);
	const hasParent = !!parent;
	const parentSelector = useCallback(() => {
		return parent ?? document.body;
	}, [parent]);

	const showProgress = progress || progress === 0;
	return (
		<StyledModal
			isOpen={open}
			onRequestClose={onClose}
			parentSelector={parentSelector}
			$hasParent={hasParent}
			closeTimeoutMS={ANIMATION_MS}
		>
			<StyledDialog
				className={className}
			>
				<StyledHeader>
					<StyledTitleContainer>
						{showProgress && <StyledProgressContainer>
							<DialogProgress progress={progress} />
						</StyledProgressContainer>}
						<StyledTitle>{title}</StyledTitle>
					</StyledTitleContainer>
					<StyledCloseIcon icon="x" onClick={onClose} />
				</StyledHeader>
				{children}
			</StyledDialog>
		</StyledModal>);
};

Dialog.Actions = StyledActions;
Dialog.Action = StyledAction;
Dialog.Message = StyledMessage;
Dialog.SubTitle = StyledSubTitle;
